import { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import Chart from "react-apexcharts";
import { fetchProjectWiseHours, fetchAllProjectWiseHours } from "../../../API_Service/Dashboard_Slice";
import { companyID, DashboardCard, EmptyState, ChartSkeleton, InlineAlert } from "../../../Common/UIComponents";
import { useDashboardSignalR } from "../../../Common/useDashboardSignalR";
import {
  HOURS_COLORS,
  axisLabelStyle,
  axisTitleStyle,
  gridBase,
  tooltipStyleBase,
  tooltipBoxStyle,
  dataLabelStyle,
  chartChrome,
  CHART_FONT,
} from "../../../Common/chartTheme";


const truncateProjectLabel = (name, max = 6) =>
  name && name.length > max ? `${name.slice(0, max)}…` : name;


const buildProjectTooltip = (fullNames) => (opts) => {
  const { series, dataPointIndex, w } = opts;
  const name = fullNames[dataPointIndex] ?? w.globals.labels[dataPointIndex] ?? "";
  const seriesNames = w.globals.seriesNames || [];
  const rows = seriesNames
    .map((seriesName, i) => {
      const value = series[i]?.[dataPointIndex];
      const display = value ? `${Number(value).toFixed(2)} hrs` : "0 hrs";
      const color = w.globals.colors[i];
      return `
        <div style="display:flex;align-items:center;gap:8px;padding:3px 0;">
          <span style="width:8px;height:8px;border-radius:50%;background:${color};flex-shrink:0;"></span>
          <span style="color:#475569;white-space:nowrap;">${seriesName}:</span>
          <span style="font-weight:700;color:#0f172a;margin-left:auto;padding-left:12px;white-space:nowrap;">${display}</span>
        </div>`;
    })
    .join("");
  return `
    <div style="padding:10px 14px;font-family:${CHART_FONT};font-size:12px;width:220px;">
      <div style="font-weight:700;color:#0f172a;margin-bottom:6px;white-space:normal;word-break:break-word;">${name}</div>
      ${rows}
    </div>`;
};

const annotateAxisLabelTitles = (containerEl, fullNames) => {
  if (!containerEl) return;
  const texts = containerEl.querySelectorAll(".apexcharts-xaxis-texts-g text");
  texts.forEach((textEl, i) => {
    textEl.querySelector("title")?.remove();
    if (!fullNames[i]) return;
    const title = document.createElementNS("http://www.w3.org/2000/svg", "title");
    title.textContent = fullNames[i];
    textEl.appendChild(title);
  });
};

const ProjectWiseHours = ({ onProjectSelected, selectedProjectID = 0, onChartHeightChange, onProjectListLoaded }) => {
  const dispatch = useDispatch();
  const [series, setSeries] = useState([]);
  const [options, setOptions] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEmpty, setIsEmpty] = useState(false);
  const [projectData, setProjectData] = useState([]);
  const [chartHeight, setChartHeight] = useState(400);
  const chartRef = useRef(null);
  const modalChartRef = useRef(null);
  const projectDataRef = useRef([]);

  const [showAllModal, setShowAllModal] = useState(false);
  const [allLoading, setAllLoading] = useState(false);
  const [allError, setAllError] = useState(null);
  const [allSeries, setAllSeries] = useState([]);
  const [allOptions, setAllOptions] = useState(null);
  const [allChartHeight, setAllChartHeight] = useState(400);
  const [allProjectData, setAllProjectData] = useState([]);

  const handleChartBarClick = useCallback((index) => {
    if (!projectData || !projectData[index]) {
      console.warn("⚠️ Invalid index for project click:", index);
      return;
    }

    const selectedProject = projectData[index];
    const projectID = selectedProject.projectId || selectedProject.ProjectId || 0;
    const projectName = selectedProject.projectName || selectedProject.ProjectName || "Unknown";

    console.log("✅ Bar Clicked:", projectName, "ID:", projectID);

    if (onProjectSelected && typeof onProjectSelected === "function") {
      console.log("🚀 Calling onProjectSelected with ID:", projectID, "Name:", projectName);
      onProjectSelected(projectID, projectName);
    }
  }, [projectData, onProjectSelected]);

  const loadProjectWiseHours = useCallback(() => {
    if (!companyID) {
      setError("Company ID not found");
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    setIsEmpty(false);

    dispatch(fetchProjectWiseHours({ companyID }))
      .then((result) => {
        console.log("🔍 Full Result:", result);
        console.log("📦 Result Payload:", result.payload);

        let data = result.payload;

        // Extract data from nested structure if it exists
        if (result.payload && result.payload.data && Array.isArray(result.payload.data)) {
          data = result.payload.data;
          console.log("✅ Found nested data array:", data);
        } else if (Array.isArray(result.payload)) {
          data = result.payload;
          console.log("✅ Direct array payload:", data);
        }

        console.log("📊 Extracted Data:", data);

        if (!Array.isArray(data) || data.length === 0) {
          setIsEmpty(true);
          setIsLoading(false);
          return;
        }

        // Extract field names from first item to debug
        const firstItem = data[0];
        console.log("🔑 First Item Keys:", Object.keys(firstItem));
        console.log("📌 First Item:", firstItem);

        const fullProjectNames = data.map((p, index) => {
          const name = p.projectName || p.ProjectName;
          const id = p.projectId || p.ProjectId;
          console.log(`[INDEX ${index}] Project: ${name}, ID: ${id}, Full Object:`, p);
          return name || "Unknown";
        });
   
        const projectNames = fullProjectNames.map((n) =>
          truncateProjectLabel(n, 6),
        );

        console.log("📊 CHART MAPPING:");
        projectNames.forEach((name, idx) => {
          console.log(`  Y-axis position ${idx}: ${name}`);
        });

        const billableHours = data.map((p) => parseFloat(p.billableHours || p.BillableHours) || 0);
        const nonBillableHours = data.map(
          (p) => parseFloat(p.nonBillableHours || p.NonBillableHours) || 0
        );

        console.log("📋 Project Names:", projectNames);
        console.log("💰 Billable Hours:", billableHours);
        console.log("⏱️ Non-Billable Hours:", nonBillableHours);

        console.log("🎨 Setting chart series and options...");

        // Store project data for click handlers - BOTH in state and ref
        setProjectData(data);
        projectDataRef.current = data;
        console.log("💾 Stored data in ref. Total items:", data.length);

        // Pass project list to parent for use in other components
        if (onProjectListLoaded && typeof onProjectListLoaded === "function") {
          const projectList = data.map((d) => ({
            id: d.projectId || d.ProjectId || 0,
            name: d.projectName || d.ProjectName || "Unknown",
          }));
          onProjectListLoaded(projectList);
          console.log("📤 Sent project list to parent:", projectList);
        }

        setSeries([
          {
            name: "Billable Hours",
            data: billableHours,
          },
          {
            name: "Non-Billable Hours",
            data: nonBillableHours,
          },
        ]);

        // Smart chart height calculation - Compact sizes
        let calculatedHeight;
        if (projectNames.length === 1) {
          calculatedHeight = 200; // Single item - compact
        } else if (projectNames.length === 2) {
          calculatedHeight = 250; // Two items
        } else {
          calculatedHeight = Math.min(40 * projectNames.length, 450); // Scale but cap at 450
        }

        // Adjust bar height percentage based on number of projects
        let barHeightPercent = "65%";
        if (projectNames.length === 1) {
          barHeightPercent = "40%"; // Single bar - smaller percentage
        } else if (projectNames.length === 2) {
          barHeightPercent = "50%"; // Two bars
        }

        setChartHeight(calculatedHeight);
        if (onChartHeightChange) {
          onChartHeightChange(calculatedHeight);
        }
        console.log("📏 Chart Height:", calculatedHeight, "Bar Height:", barHeightPercent, "for", projectNames.length, "projects");

        // Create click handler - detect which bar by finding clicked element in SVG
        const handleBarClick = (event, _, config) => {
          console.log("\n🖱️ ====== CLICK EVENT ======");

          let dataPointIndex = config?.dataPointIndex;
          const currentData = projectDataRef.current;

          console.log("📈 ApexCharts dataPointIndex:", dataPointIndex);

          // FALLBACK: Try to find which bar was actually clicked by examining the DOM
          if ((dataPointIndex === undefined || dataPointIndex === null || dataPointIndex < 0) && event && currentData) {
            console.log("⚠️ Using fallback: Finding clicked bar from SVG elements...");

            // Get all bar rectangles in the chart
            const svg = chartRef.current?.querySelector("svg");
            if (svg && event.target) {
              let clickedElement = event.target;

              // Walk up the DOM tree to find the bar group or rect
              while (clickedElement && clickedElement !== svg) {
                const g = clickedElement.closest("g[data-pointIndex]");
                if (g) {
                  const pointIndex = g.getAttribute("data-pointIndex");
                  if (pointIndex !== null) {
                    dataPointIndex = parseInt(pointIndex, 10);
                    console.log("✅ Found bar via data-pointIndex attribute:", dataPointIndex);
                    break;
                  }
                }
                clickedElement = clickedElement.parentElement;
              }

              // If still not found, try getting all bars and finding by Y position
              if (dataPointIndex === undefined || dataPointIndex < 0) {
                const allBars = svg.querySelectorAll("[data-pointIndex]");
                console.log("📊 Found", allBars.length, "bar elements in SVG");

                if (allBars.length > 0 && event.clientY !== undefined) {
                  const closestBar = Array.from(allBars).reduce((closest, bar) => {
                    const barRect = bar.getBoundingClientRect();
                    const barCenter = barRect.top + barRect.height / 2;
                    const distance = Math.abs(event.clientY - barCenter);

                    const closestRect = closest?.getBoundingClientRect();
                    const closestCenter = closestRect?.top + closestRect?.height / 2;
                    const closestDistance = closest ? Math.abs(event.clientY - closestCenter) : Infinity;

                    return distance < closestDistance ? bar : closest;
                  });

                  if (closestBar) {
                    const idx = closestBar.getAttribute("data-pointIndex");
                    if (idx !== null) {
                      dataPointIndex = parseInt(idx, 10);
                      console.log("📍 Closest bar by Y position: index=" + dataPointIndex);
                    }
                  }
                }
              }
            }
          }

          // Validate and use the index
          if (dataPointIndex !== undefined && dataPointIndex !== null && dataPointIndex >= 0 && dataPointIndex < currentData.length) {
            console.log("✅ Valid index:", dataPointIndex);

            const selectedProject = currentData[dataPointIndex];
            console.log("📌 Project at index", dataPointIndex, ":", selectedProject);

            const projectID = selectedProject.projectId || selectedProject.ProjectId || 0;
            const projectName = selectedProject.projectName || selectedProject.ProjectName || "Unknown";

            console.log("✨ Sending: Name=" + projectName + " | ID=" + projectID);

            if (onProjectSelected && typeof onProjectSelected === "function") {
              onProjectSelected(projectID, projectName);
            }
          } else {
            console.error("❌ ERROR: dataPointIndex=" + dataPointIndex + " (valid: 0 to " + (currentData?.length - 1) + ")");
          }

          console.log("🖱️ ====== END CLICK ======\n");
        };

        setOptions({
          chart: {
            ...chartChrome,
            type: "bar",
            stacked: true,
            toolbar: {
              show: true,
              tools: {
                download: true,
                selection: true,
                zoom: true,
                zoomin: true,
                zoomout: true,
                pan: true,
                reset: true,
              },
              offsetX: 0,
              offsetY: 0,
            },
            height: chartHeight,
            animations: {
              enabled: true,
              speed: 800,
              animateGradually: {
                enabled: true,
                delay: 150,
              },
              dynamicAnimation: {
                enabled: true,
                speed: 150,
              },
            },
            events: {
              mounted: () => annotateAxisLabelTitles(chartRef.current, fullProjectNames),
              updated: () => annotateAxisLabelTitles(chartRef.current, fullProjectNames),
            },
          },
          plotOptions: {
            bar: {
              horizontal: true,
              distributed: false,
              dataLabels: {
                position: "top",
              },
              barHeight: barHeightPercent,
              borderRadius: 6,
              borderRadiusApplication: "end",
            },
          },
          events: {
            click: (event, chartContext, config) => {
              console.log("📊 Chart click - DataPointIndex:", config?.dataPointIndex);
              handleBarClick(event, chartContext, config);
            },
            dataPointSelection: (event, chartContext, config) => {
              console.log("🎯 DataPointSelection - Index:", config?.dataPointIndex);
              handleBarClick(event, chartContext, config);
            },
            xAxisLabelClick: (event, chartContext, config) => {
              console.log("🏷️ XAxis label clicked - Config:", config);
              handleBarClick(event, chartContext, config);
            },
          },
          dataLabels: {
            enabled: true,
            formatter: (value) => {
              return value ? value.toFixed(2) : "0.00";
            },
            style: dataLabelStyle,
            background: {
              enabled: false,
            },
            offsetX: -5,
            textAnchor: "end",
          },
          stroke: {
            show: true,
            width: 0,
            colors: ["transparent"],
          },
          xaxis: {
            categories: projectNames,
            type: "category",
            labels: {
              style: axisLabelStyle,
              offsetY: 5,
            },
            axisBorder: {
              show: true,
              color: "#e2e8f0",
              height: 1,
            },
            axisTicks: {
              show: false,
            },
            title: {
              text: "Hours",
              style: axisTitleStyle,
              offsetY: 10,
            },
          },
          yaxis: {
            type: "numeric",
            axisBorder: {
              show: true,
              color: "#e2e8f0",
              width: 1,
            },
            axisTicks: {
              show: false,
            },
            labels: {
              style: axisLabelStyle,
              offsetX: -5,
            },
            title: {
              text: "Projects",
              style: axisTitleStyle,
              offsetY: -25,
            },
          },
          fill: {
            opacity: 1,
            type: "solid",
          },
          grid: {
            ...gridBase,
            xaxis: {
              lines: {
                show: true,
                opacity: 0.1,
              },
            },
            yaxis: {
              lines: {
                show: false,
              },
            },
            padding: {
              left: 0,
              right: 10,
              top: 0,
              bottom: 0,
            },
          },
          states: {
            hover: {
              filter: {
                type: "darken",
                value: 0.15,
              },
            },
            active: {
              filter: {
                type: "darken",
                value: 0.15,
              },
            },
          },
          colors: HOURS_COLORS,
          tooltip: {
            enabled: true,
            theme: "light",
            style: { ...tooltipStyleBase, ...tooltipBoxStyle },
            onDatasetHover: {
              highlightDataSeries: true,
            },
            shared: true,
            intersect: false,
            fillSeriesColor: false,
            custom: buildProjectTooltip(fullProjectNames),
          },
          responsive: [
            {
              breakpoint: 1024,
              options: {
                plotOptions: {
                  bar: {
                    barHeight: "60%",
                  },
                },
              },
            },
          ],
        });

        setError(null);
      })
      .catch((err) => {
        console.error("ProjectWiseHours Error:", err);
        setError(err?.message || "Failed to load project-wise hours");
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [companyID, dispatch]);

  useEffect(() => {
    loadProjectWiseHours();
  }, [loadProjectWiseHours]);

  const loadAllProjects = useCallback(() => {
    if (!companyID) {
      setAllError("Company ID not found");
      return;
    }

    setAllLoading(true);
    setAllError(null);

    dispatch(fetchAllProjectWiseHours({ companyID }))
      .then((result) => {
        let data = result.payload;

        if (result.payload && result.payload.data && Array.isArray(result.payload.data)) {
          data = result.payload.data;
        } else if (Array.isArray(result.payload)) {
          data = result.payload;
        }

        if (!Array.isArray(data) || data.length === 0) {
          setAllError("No project data available");
          setAllLoading(false);
          return;
        }

        const fullProjectNames = data.map((p) => p.projectName || p.ProjectName || "Unknown");
        const projectNames = fullProjectNames.map((n) => truncateProjectLabel(n, 6));
        const billableHours = data.map((p) => parseFloat(p.billableHours || p.BillableHours) || 0);
        const nonBillableHours = data.map((p) => parseFloat(p.nonBillableHours || p.NonBillableHours) || 0);

        setAllProjectData(data);

        setAllSeries([
          { name: "Billable Hours", data: billableHours },
          { name: "Non-Billable Hours", data: nonBillableHours },
        ]);

        const calculatedHeight = Math.max(300, 40 * projectNames.length);
        setAllChartHeight(calculatedHeight);

        setAllOptions({
          chart: {
            ...chartChrome,
            type: "bar",
            stacked: true,
            toolbar: {
              show: true,
              tools: { download: true },
            },
            animations: { enabled: true, speed: 400 },
            events: {
              mounted: () => annotateAxisLabelTitles(modalChartRef.current, fullProjectNames),
              updated: () => annotateAxisLabelTitles(modalChartRef.current, fullProjectNames),
            },
          },
          plotOptions: {
            bar: {
              horizontal: true,
              barHeight: projectNames.length > 20 ? "70%" : "60%",
              borderRadius: 6,
              borderRadiusApplication: "end",
              dataLabels: { position: "top" },
            },
          },
          dataLabels: {
            enabled: true,
            formatter: (value) => (value ? value.toFixed(2) : "0.00"),
            style: dataLabelStyle,
            offsetX: -5,
            textAnchor: "end",
          },
          stroke: { show: true, width: 0, colors: ["transparent"] },
          xaxis: {
            categories: projectNames,
            type: "category",
            labels: {
              style: axisLabelStyle,
            },
            title: {
              text: "Hours",
              style: axisTitleStyle,
            },
          },
          yaxis: {
            labels: {
              style: axisLabelStyle,
            },
            title: {
              text: "Projects",
              style: axisTitleStyle,
              offsetY: -25,
            },
          },
          fill: { opacity: 1, type: "solid" },
          colors: HOURS_COLORS,
          grid: {
            ...gridBase,
            xaxis: { lines: { show: true, opacity: 0.1 } },
            yaxis: { lines: { show: false } },
          },
          tooltip: {
            enabled: true,
            theme: "light",
            shared: true,
            intersect: false,
            style: tooltipStyleBase,
            custom: buildProjectTooltip(fullProjectNames),
          },
        });

        setAllError(null);
      })
      .catch((err) => {
        console.error("All Project-wise Hours Error:", err);
        setAllError(err?.message || "Failed to load all project-wise hours");
      })
      .finally(() => {
        setAllLoading(false);
      });
  }, [dispatch]);

  const handleOpenAllProjectsModal = () => {
    setShowAllModal(true);
    loadAllProjects();
  };

  // Add custom click handler for chart SVG - handles small bars better
  useEffect(() => {
    const chartElement = chartRef.current?.querySelector(".apexcharts-canvas");
    console.log("🔌 ProjectWiseHours click handler - Attaching to:", chartElement ? "✅ Found" : "❌ Not found");

    if (!chartElement) {
      console.warn("⚠️ Chart element not found for ProjectWiseHours");
      return;
    }

    const handleDirectClick = (e) => {
      console.log("\n🖱️ ====== DIRECT SVG CLICK ======");

      const currentData = projectDataRef.current;
      console.log("📦 Data array length:", currentData?.length);

      if (!currentData || currentData.length === 0) {
        console.log("⚠️ No data available");
        return;
      }

      // Try to find the bar rect element
      let barElement = e.target.closest("rect");
      if (!barElement) {
        console.log("⚠️ No rect element found");
        return;
      }

      console.log("✅ Found rect element, investigating structure...");

      // SIMPLE & RELIABLE METHOD: Get all bar rects, sort by Y position, find which one was clicked
      const svg = chartRef.current?.querySelector("svg");
      if (!svg) {
        console.log("❌ No SVG found");
        return;
      }

      // Get ALL bar rectangles (serie 0 - billable hours)
      const allBarRects = Array.from(svg.querySelectorAll("rect.apexcharts-bar-serie0"));
      console.log("📊 Querying for: rect.apexcharts-bar-serie0");
      console.log("📊 Found", allBarRects.length, "bar rectangles");

      // If none found, try alternative selector
      if (allBarRects.length === 0) {
        console.log("⚠️ No bars with serie0 class, trying all bars...");
        const allBars = Array.from(svg.querySelectorAll("rect[x][y][width][height]"));
        console.log("📊 Found", allBars.length, "total rects with dimensions");
      }

      if (allBarRects.length === 0) {
        console.log("❌ No bars found in chart");
        return;
      }

      // Get bounding rectangles for all bars and sort by Y position (top to bottom)
      const barsWithBounds = allBarRects.map((rect, idx) => ({
        rect,
        index: idx,
        bounds: rect.getBoundingClientRect(),
      }));

      barsWithBounds.sort((a, b) => a.bounds.top - b.bounds.top);

      console.log("📊 Bars sorted by Y position:");
      barsWithBounds.forEach((item, i) => {
        console.log(`  [${i}] Y: ${item.bounds.top.toFixed(0)}, Height: ${item.bounds.height.toFixed(0)}`);
      });

      // Find which bar contains the click point or is closest
      let dataPointIndex = null;
      const clickY = e.clientY;

      for (let i = 0; i < barsWithBounds.length; i++) {
        const item = barsWithBounds[i];
        if (clickY >= item.bounds.top && clickY <= item.bounds.bottom) {
          dataPointIndex = i;
          console.log("✅ Click is INSIDE bar at index", i);
          break;
        }
      }

      // If not inside any bar, find the closest one
      if (dataPointIndex === null) {
        console.log("⚠️ Click not inside any bar, finding closest...");
        let closestIdx = 0;
        let closestDistance = Infinity;

        for (let i = 0; i < barsWithBounds.length; i++) {
          const item = barsWithBounds[i];
          const barCenter = item.bounds.top + item.bounds.height / 2;
          const distance = Math.abs(clickY - barCenter);

          console.log(`  [${i}] Distance: ${distance.toFixed(0)}`);

          if (distance < closestDistance) {
            closestDistance = distance;
            closestIdx = i;
          }
        }

        dataPointIndex = closestIdx;
        console.log("📍 Closest bar is at index:", dataPointIndex, "distance:", closestDistance.toFixed(0));
      }

      // Validate and use the index
      console.log("🎯 Final index:", dataPointIndex, "Valid range: 0 to", currentData.length - 1);

      if (dataPointIndex !== null && dataPointIndex >= 0 && dataPointIndex < currentData.length) {
        const selectedProject = currentData[dataPointIndex];
        const projectID = selectedProject.projectId || selectedProject.ProjectId || 0;
        const projectName = selectedProject.projectName || selectedProject.ProjectName || "Unknown";

        console.log("✨ Selected Project:");
        console.log("  Name:", projectName);
        console.log("  ID:", projectID);
        console.log("  Data:", selectedProject);

        if (onProjectSelected && typeof onProjectSelected === "function") {
          onProjectSelected(projectID, projectName);
          console.log("✅ Callback executed!");
        }
      } else {
        console.error("❌ ERROR: Invalid index", dataPointIndex);
      }

      console.log("🖱️ ====== END CLICK ======\n");
    };

    chartElement.addEventListener("click", handleDirectClick);
    return () => chartElement.removeEventListener("click", handleDirectClick);
  }, [onProjectSelected]);

  useDashboardSignalR(loadProjectWiseHours);

  if (error) {
    return <InlineAlert type="error" message={`Error loading project-wise hours: ${error}`} />;
  }

  if (isEmpty) {
    return (
      <DashboardCard icon="bar_chart" title="Project-wise Hours Consumption">
        <EmptyState message="No project data available" />
      </DashboardCard>
    );
  }

  if (isLoading || !options) {
    return (
      <DashboardCard icon="bar_chart" title="Project-wise Hours Consumption">
        <ChartSkeleton height={256} />
      </DashboardCard>
    );
  }

  return (
    <>
    <DashboardCard
      icon="bar_chart"
      title="Project-wise Hours Consumption"
      subtitle="Click on a project below to filter Test Type Analysis"
      actions={
        <button
          type="button"
          onClick={handleOpenAllProjectsModal}
          className="px-3 py-1.5 text-xs font-semibold text-[#08757C] border border-[#08757C]/30 rounded-md hover:bg-[#08757C]/5 transition-colors"
        >
          View All Projects
        </button>
      }
    >
      {/* Legend */}
      <div className="flex items-center gap-6 mb-4 pb-4 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: HOURS_COLORS[0] }}></div>
          <span className="text-sm font-medium text-slate-600">Billable Hours</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: HOURS_COLORS[1] }}></div>
          <span className="text-sm font-medium text-slate-600">Non-Billable Hours</span>
        </div>
      </div>

      {/* Chart */}
      {series.length > 0 && options ? (
        <div
          ref={chartRef}
          className="chart-fade-in rounded-xl overflow-hidden bg-white p-4 cursor-pointer"
          onClick={(e) => {
            // Improved click detection for ApexCharts bars
            const svgElement = e.target.closest('svg');
            if (!svgElement) {
              console.log("❌ No SVG element found");
              return;
            }

            const rect = svgElement.getBoundingClientRect();
            const clickX = e.clientX - rect.left;
            const clickY = e.clientY - rect.top;

            // Find the actual bar element that was clicked
            const clickedElement = document.elementFromPoint(e.clientX, e.clientY);
            console.log("🎯 Clicked element:", clickedElement?.tagName, clickedElement?.getAttribute('data-pointindex'));

            // Try multiple detection methods
            let detectedIndex = null;

            // Method 1: Check for data-pointindex attribute
            let barElement = clickedElement?.closest('[data-pointindex]');
            if (barElement) {
              detectedIndex = parseInt(barElement.getAttribute('data-pointindex'), 10);
              console.log("✅ Method 1 - Found via data-pointindex:", detectedIndex);
            }

            // Method 2: Find parent rect (bar element) in SVG
            if (detectedIndex === null) {
              barElement = clickedElement?.closest('rect[x]');
              if (barElement && barElement.getAttribute('x') !== null) {
                // Get y position to determine which bar
                const barY = parseFloat(barElement.getAttribute('y'));
                const yThreshold = svgElement.clientHeight / (projectData.length || 1);
                const estimatedIndex = Math.round(barY / yThreshold);
                if (estimatedIndex >= 0 && estimatedIndex < projectData.length) {
                  detectedIndex = estimatedIndex;
                  console.log("✅ Method 2 - Found via rect position:", detectedIndex);
                }
              }
            }

            // Method 3: Fallback to Y position calculation
            if (detectedIndex === null) {
              const chartHeight = svgElement.clientHeight;
              const barHeight = chartHeight / (projectData.length || 1);
              detectedIndex = Math.floor(clickY / barHeight);
              console.log("✅ Method 3 - Fallback Y position:", detectedIndex);
            }

            console.log("📊 Click Position - X:", clickX, "Y:", clickY, "Final Index:", detectedIndex);

            if (detectedIndex !== null && detectedIndex >= 0 && detectedIndex < projectData.length) {
              console.log("✅ Valid bar index detected:", detectedIndex);
              handleChartBarClick(detectedIndex);
            } else {
              console.warn("⚠️ Invalid index:", detectedIndex, "Max:", projectData.length - 1);
            }
          }}
        >
          <Chart
            options={options}
            series={series}
            type="bar"
            height={chartHeight}
          />
        </div>
      ) : (
        <EmptyState message="No data available" />
      )}
    </DashboardCard>

    {/* View All Projects Modal */}
    {showAllModal && (
      <div
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        onClick={() => setShowAllModal(false)}
      >
        <div
          className="bg-white rounded-lg shadow-xl border border-slate-200/80 w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <div>
              <h3 className="text-sm font-semibold text-slate-800">All Projects - Hours Consumption</h3>
              <p className="text-xs text-slate-500 mt-0.5">Complete list of all projects</p>
            </div>
            <button
              type="button"
              onClick={() => setShowAllModal(false)}
              className="p-1.5 hover:bg-slate-100 rounded-md transition-colors"
            >
              <span className="material-symbols-outlined text-slate-500 text-[20px]">close</span>
            </button>
          </div>

          {/* Modal Body */}
          <div className="flex-1 overflow-y-auto p-4">
            {allLoading ? (
              <ChartSkeleton height={256} />
            ) : allError ? (
              <InlineAlert type="error" message={`Error: ${allError}`} />
            ) : allSeries.length > 0 && allOptions ? (
              <>
                {/* Legend */}
                <div className="flex items-center gap-6 mb-4 pb-3 border-b border-slate-100">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: HOURS_COLORS[0] }}></div>
                    <span className="text-sm font-medium text-slate-600">Billable Hours</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: HOURS_COLORS[1] }}></div>
                    <span className="text-sm font-medium text-slate-600">Non-Billable Hours</span>
                  </div>
                  <div className="ml-auto text-sm text-slate-500">
                    Total Projects: <span className="font-bold text-slate-900">{allProjectData.length}</span>
                  </div>
                </div>

                <div ref={modalChartRef}>
                  <Chart
                    options={allOptions}
                    series={allSeries}
                    type="bar"
                    height={allChartHeight}
                  />
                </div>
              </>
            ) : (
              <EmptyState message="No data available" />
            )}
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default ProjectWiseHours;
